import { DocumentNode, gql, useQuery } from "@apollo/client";
import React from "react";
import { OrganizationContext } from "../contexts/organization";
import { useAuth } from "react-oidc-context";

function useUser() {
  const [userID, setUserID] = React.useState<number | undefined>();
  const [username, setUsername] = React.useState<string | undefined>();
  const [organizationID, setOrganizationID] = React.useState<number | undefined>();
  const [organizationUUID, setOrganizationUUID] = React.useState<string | undefined>();
  const [authenticatedUserEmail, setAuthenticatedUserEmail] = React.useState<string | undefined>();
  const [authenticatedUserName, setAuthenticatedUserName] = React.useState<string | undefined>();
  const [authenticatedUserGivenName, setAuthenticatedUserGivenName] = React.useState<string | undefined>();
  const [authenticatedUserFamilyName, setAuthenticatedUserFamilyName] = React.useState<string | undefined>();
  const [welcomeFormSub, setWelcomeFormSub] = React.useState<boolean | undefined>();
  const [roles, setRoles] = React.useState<string[] | undefined>();

  const organizationCtx = React.useContext(OrganizationContext);

  const { user } = useAuth();

  let query: DocumentNode;
  if (organizationCtx && organizationCtx.organizationID) {
    query = gql(`
      query GetUser {
        users {
          id
          username
          email
          welcome_form_sub
        }
        organizations(where: {uuid: {_eq: "${organizationCtx.organizationID}"}}) {
          id
        }
      }`);
  } else {
    query = gql(`
      query GetUser {
        users {
          id
          username
          email
          welcome_form_sub
        }
      }`);
  }

  const { loading, error, data } = useQuery(query);

  React.useEffect(() => {
    if (data && authenticatedUserEmail) {
      const user = data.users.find((user: any) => user.email === authenticatedUserEmail);
      setUserID(user?.id);
      setUsername(user?.username);
      if (data.organizations && data.organizations.length > 0) {
        setOrganizationID(data.organizations[0].id);
      } else {
        setOrganizationID(undefined);
      }
      setWelcomeFormSub(user?.welcome_form_sub ?? false);
    }
  }, [data, authenticatedUserEmail]);

  React.useEffect(() => {
    if (organizationCtx && organizationCtx.organizationID) {
      setOrganizationUUID(organizationCtx.organizationID);
    } else {
      setOrganizationUUID(undefined);
    }
  }, [organizationCtx]);

  React.useEffect(() => {
    setAuthenticatedUserEmail(user?.profile.email);
    setAuthenticatedUserName(user?.profile.name);
    setAuthenticatedUserGivenName(user?.profile.given_name);
    setAuthenticatedUserFamilyName(user?.profile.family_name);
    setRoles((user?.profile?.roles as []) ?? []);
  }, [user]);

  const hasRole = React.useCallback((role?: string) => {
    if (!role) {
      return true;
    }

    if (!user) {
      return undefined;
    }

    return roles?.includes(role);
  }, [roles]);

  // teams
  const teamHierarchy = ['maverick', 'galaxia', 'vega'];
  const team = teamHierarchy.find((team) => hasRole(team));

  return {
    authenticatedUserEmail,
    authenticatedUserName,
    authenticatedUserGivenName,
    authenticatedUserFamilyName,
    userID,
    username,
    organizationID,
    organizationUUID,
    hasRole,
    team,
    welcomeFormSub,
  };
}

export default useUser;
